import { useMemo } from 'react';
import prettyBytes from 'pretty-bytes';

import { ColumnDef } from '@dbt-labs/sourdough';

import { PageHeading } from '@/components/PageHeading';
import { getNameLinkCell } from '@/components/Table/LinkCell';
import { OneLineHeader } from '@/components/Table/TableHeader';
import { RightAlignedTruncatedCell, TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useLinkToExplorer } from '@/dbt-explorer';
import { useDiscoveryAppliedSnapshots } from '@/hooks/discovery/appliedSnapshots';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { getStringStat, ResourceStat } from '@/util/discoveryStat';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

const ADAPTERS_WITH_LAST_MODIFIED_STAT = new Set<string>(['snowflake']);

type TableData = {
  name: string;
  uniqueId: string;
  lastModified: string | undefined;
  rowCount: string | undefined;
  byteSize: string | undefined;
};

type SnapshotAppliedListNode = {
  uniqueId: string;

  catalog: {
    rowCountStat: number | null;
    bytesStat: number | null;
    stats: ResourceStat[] | null;
  } | null;
};

const getTableData = (snapshot: SnapshotAppliedListNode): TableData => {
  const uniqueId = parseUniqueId(snapshot.uniqueId);

  const lastModified = getStringStat(snapshot.catalog?.stats, 'last_modified');
  const rowCount = snapshot?.catalog?.rowCountStat ?? undefined;
  const byteSize = snapshot?.catalog?.bytesStat ?? undefined;

  return {
    name: uniqueId.remainder,
    uniqueId: uniqueId.raw,
    lastModified,
    rowCount: rowCount?.toLocaleString(),
    byteSize: byteSize ? prettyBytes(byteSize) : undefined,
  };
};

export const SnapshotFilterView = () => {
  const pageSize = INITIAL_PAGE_SIZE;
  const { environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const projectScope = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const paginatedSnapshotsQuery = useDiscoveryAppliedSnapshots({
    environmentId,
    userId,
    first: pageSize,
  });
  const snapshots = paginatedSnapshotsQuery.data;
  const pageInfo = paginatedSnapshotsQuery.pageInfo;
  const adapterType = pageInfo?.metadata?.adapterType;

  const data = useMemo((): any[] | undefined => {
    return snapshots?.map(getTableData);
  }, [snapshots]);

  const columns = useMemo(() => {
    const columns: ColumnDef<any, any>[] = [
      {
        header: 'Name',
        accessorKey: 'name',
        id: 'name',
        cell: getNameLinkCell(getDetailsPageLink, projectScope),
      },
      {
        header: () => <OneLineHeader title="Row count" align="right" />,
        accessorKey: 'rowCount',
        size: 110,
        cell: RightAlignedTruncatedCell,
        meta: {
          align: 'right',
        },
      },
      {
        header: () => <OneLineHeader title="Size" align="right" />,
        accessorKey: 'byteSize',
        size: 110,
        cell: RightAlignedTruncatedCell,
        meta: {
          align: 'right',
        },
      },
    ];

    if (adapterType && ADAPTERS_WITH_LAST_MODIFIED_STAT.has(adapterType)) {
      columns.push({
        header: 'Last modified',
        accessorKey: 'lastModified',
        size: 70,
        minSize: 100,
        cell: TruncatedCell,
      });
    }

    return columns;
  }, [adapterType, getDetailsPageLink, projectScope]);

  if (paginatedSnapshotsQuery.error) return <>{paginatedSnapshotsQuery.error.stack}</>;
  const isLoadingMore = paginatedSnapshotsQuery.fetching;
  const isInitialLoad =
    paginatedSnapshotsQuery.fetching && paginatedSnapshotsQuery.data === undefined;

  return (
    <>
      <PageHeading className="mb-10">Snapshots</PageHeading>
      <ResourceFilterTable
        pluralizedResultName="snapshots"
        data={data}
        columns={columns}
        pageInfo={pageInfo}
        isLoading={isInitialLoad}
        isLoadingMore={isLoadingMore}
        pageSize={pageSize}
        fetchMore={paginatedSnapshotsQuery.fetchMore}
      />
    </>
  );
};
